import type {
  ListSubscriptionsApiRawResponse,
  ListSubscriptionsApiResponse,
  SubscriptionListApiRow,
  SubscriptionListItem,
} from "../list-subscriptions/types";

function parseRevenueByCurrency(raw: unknown): Record<string, number> | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "number" && Number.isFinite(v)) {
      out[k] = v;
      continue;
    }
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) {
        out[k] = n;
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeLegacyRevenueValue(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    return value.trim();
  }
  return undefined;
}

function pickLegacyTotalRevenue(o: Record<string, unknown>): string {
  return (
    normalizeLegacyRevenueValue(o.totalRevenueInDefaultCurrency) ??
    normalizeLegacyRevenueValue(o.total_revenue_in_default_currency) ??
    normalizeLegacyRevenueValue(o.totalRevenue) ??
    normalizeLegacyRevenueValue(o.total_revenue) ??
    ""
  );
}

/**
 * Match `defaultCurrency` to a key in the map (exact, then case-insensitive).
 */
function lookupRevenueForCurrency(
  revenueByCurrency: Record<string, number>,
  currencyCode: string
): number | undefined {
  const trimmed = currencyCode.trim();
  if (!trimmed) {
    return undefined;
  }
  if (
    Object.prototype.hasOwnProperty.call(revenueByCurrency, trimmed) &&
    Number.isFinite(revenueByCurrency[trimmed])
  ) {
    return revenueByCurrency[trimmed];
  }
  const upper = trimmed.toUpperCase();
  for (const [k, v] of Object.entries(revenueByCurrency)) {
    if (k.toUpperCase() === upper && Number.isFinite(v)) {
      return v;
    }
  }
  return undefined;
}

type RevenueDisplayResult = {
  totalRevenue: string;
  revenueDisplayCurrency?: string;
};

/**
 * Prefer default-currency slice of `revenueByCurrency`; then legacy; then a single map bucket.
 * An explicit empty `revenueByCurrency` object means no revenue yet — show 0 in base currency.
 */
function deriveTotalRevenueDisplay(
  defaultCurrency: string | undefined,
  revenueByCurrency: Record<string, number> | undefined,
  fallbackTotalRevenue: string
): RevenueDisplayResult {
  if (revenueByCurrency !== undefined && Object.keys(revenueByCurrency).length === 0) {
    return {totalRevenue: "0"};
  }

  const map = revenueByCurrency;
  const hasMap = map && Object.keys(map).length > 0;

  if (!hasMap) {
    return {totalRevenue: fallbackTotalRevenue};
  }

  if (defaultCurrency?.trim()) {
    const amount = lookupRevenueForCurrency(map!, defaultCurrency);
    if (amount !== undefined) {
      return {totalRevenue: String(amount)};
    }
  }

  if (fallbackTotalRevenue !== "") {
    return {totalRevenue: fallbackTotalRevenue};
  }

  const finiteEntries = Object.entries(map!).filter(([, v]) => Number.isFinite(v));
  if (finiteEntries.length === 1) {
    const [k, v] = finiteEntries[0];
    return {
      totalRevenue: String(v),
      revenueDisplayCurrency: k.toUpperCase(),
    };
  }

  return {totalRevenue: ""};
}

function coerceNonNegativeInt(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return Math.max(0, Math.floor(n));
    }
  }
  return fallback;
}

function coerceId(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return 0;
}

function mapSubscriptionListRow(raw: SubscriptionListApiRow): SubscriptionListItem {
  const o = raw;

  const id = coerceId(o.id);
  const name = typeof o.name === "string" ? o.name : String(o.name ?? "");
  const slug = typeof o.slug === "string" ? o.slug.trim() || undefined : undefined;
  const tinyUrlRaw = o.tinyUrl ?? o.tinyurl ?? o.tiny_url;
  const tinyUrl =
    typeof tinyUrlRaw === "string" && tinyUrlRaw.trim() !== ""
      ? tinyUrlRaw.trim()
      : undefined;
  const description =
    typeof o.description === "string"
      ? o.description
      : typeof o.summary === "string"
      ? o.summary
      : undefined;

  const totalTiers = coerceNonNegativeInt(o.totalTiers ?? o.total_tiers);
  const activeSubscribers = coerceNonNegativeInt(
    o.activeSubscribers ?? o.active_subscribers
  );

  const totalRvRaw =
    o.totalRevenueInDefaultCurrency ?? o.total_revenue_in_default_currency;
  let totalRevenueInDefaultCurrency: number | undefined;
  if (typeof totalRvRaw === "number" && Number.isFinite(totalRvRaw)) {
    totalRevenueInDefaultCurrency = totalRvRaw;
  } else if (typeof totalRvRaw === "string" && totalRvRaw.trim() !== "") {
    const n = Number(totalRvRaw);
    if (Number.isFinite(n)) {
      totalRevenueInDefaultCurrency = n;
    }
  }

  const statusName =
    typeof o.statusName === "string"
      ? o.statusName
      : typeof o.status_name === "string"
      ? o.status_name
      : undefined;
  const coverImage =
    typeof o.coverImage === "string"
      ? o.coverImage
      : typeof o.cover_image === "string"
      ? o.cover_image
      : undefined;
  const defaultCurrency =
    typeof o.defaultCurrency === "string"
      ? o.defaultCurrency
      : typeof o.default_currency === "string"
      ? o.default_currency
      : typeof o.currency === "string"
      ? o.currency
      : typeof o.defaultCurrencyCode === "string"
      ? o.defaultCurrencyCode
      : typeof o.default_currency_code === "string"
      ? o.default_currency_code
      : undefined;

  const revenueByCurrency = parseRevenueByCurrency(
    o.revenueByCurrency ?? o.revenue_by_currency
  );

  const legacyTotalRevenue = pickLegacyTotalRevenue(o);

  const {totalRevenue, revenueDisplayCurrency} = deriveTotalRevenueDisplay(
    defaultCurrency,
    revenueByCurrency,
    legacyTotalRevenue
  );

  /** Optional rollup display currency when backend names it separately. */
  const rollupCurrency =
    typeof o.revenueCurrency === "string"
      ? o.revenueCurrency
      : typeof o.revenue_currency === "string"
      ? o.revenue_currency
      : undefined;

  const userId =
    typeof o.userId === "number" && Number.isFinite(o.userId)
      ? o.userId
      : typeof o.user_id === "number" && Number.isFinite(o.user_id)
      ? o.user_id
      : undefined;

  const hideFromProfile =
    typeof o.hideFromProfile === "boolean"
      ? o.hideFromProfile
      : typeof o.hide_from_profile === "boolean"
      ? o.hide_from_profile
      : undefined;

  const requestPhoneNumber =
    typeof o.requestPhoneNumber === "boolean"
      ? o.requestPhoneNumber
      : typeof o.request_phone_number === "boolean"
      ? o.request_phone_number
      : undefined;

  const redirectUrl =
    typeof o.redirectUrl === "string" || o.redirectUrl === null
      ? (o.redirectUrl as string | null)
      : typeof o.redirect_url === "string" || o.redirect_url === null
      ? (o.redirect_url as string | null)
      : undefined;

  const bookingQuestionsRaw = o.bookingQuestions ?? o.booking_questions;
  const bookingQuestions =
    Array.isArray(bookingQuestionsRaw) &&
    bookingQuestionsRaw.every((x) => typeof x === "string")
      ? (bookingQuestionsRaw as string[])
      : undefined;

  const enableCurrencyConversion =
    typeof o.enableCurrencyConversion === "boolean"
      ? o.enableCurrencyConversion
      : typeof o.enable_currency_conversion === "boolean"
      ? o.enable_currency_conversion
      : undefined;

  const status =
    typeof o.status === "number" && Number.isFinite(o.status)
      ? o.status
      : typeof o.status === "string" && o.status.trim() !== ""
      ? Number(o.status)
      : undefined;

  const createdAt =
    typeof o.createdAt === "string"
      ? o.createdAt
      : typeof o.created_at === "string"
      ? o.created_at
      : undefined;

  const updatedAt =
    typeof o.updatedAt === "string"
      ? o.updatedAt
      : typeof o.updated_at === "string"
      ? o.updated_at
      : undefined;

  const mergedDisplayCurrency =
    revenueDisplayCurrency ??
    (rollupCurrency?.trim() ? rollupCurrency.trim().toUpperCase() : undefined) ??
    (defaultCurrency?.trim() ? defaultCurrency.trim().toUpperCase() : undefined);

  return {
    id,
    name,
    slug,
    ...(tinyUrl !== undefined ? {tinyUrl} : {}),
    description,
    coverImage,
    totalTiers,
    activeSubscribers,
    totalRevenueInDefaultCurrency,
    ...(userId !== undefined ? {userId} : {}),
    ...(hideFromProfile !== undefined ? {hideFromProfile} : {}),
    ...(requestPhoneNumber !== undefined ? {requestPhoneNumber} : {}),
    ...(redirectUrl !== undefined ? {redirectUrl} : {}),
    ...(bookingQuestions !== undefined ? {bookingQuestions} : {}),
    ...(enableCurrencyConversion !== undefined ? {enableCurrencyConversion} : {}),
    ...(typeof status === "number" && Number.isFinite(status) ? {status} : {}),
    ...(statusName !== undefined ? {statusName} : {}),
    ...(defaultCurrency !== undefined ? {defaultCurrency} : {}),
    ...(mergedDisplayCurrency ? {revenueDisplayCurrency: mergedDisplayCurrency} : {}),
    ...(revenueByCurrency !== undefined ? {revenueByCurrency} : {}),
    totalRevenue,
    ...(createdAt !== undefined ? {createdAt} : {}),
    ...(updatedAt !== undefined ? {updatedAt} : {}),
  };
}

export function normalizeSubscriptionPaginatedResponse(
  raw: ListSubscriptionsApiRawResponse
): ListSubscriptionsApiResponse {
  const page = raw.data;
  const rows = page?.data ?? [];
  return {
    subscriptions: rows.map(mapSubscriptionListRow),
    total: page?.total ?? 0,
    hasNextPage: !!page?.hasNextPage,
    hasPrevPage: !!page?.hasPrevPage,
  };
}
